import { useFormikContext } from "formik";
import * as React from "react";
import { Input, type InputProps } from "@/core/components/ui/input";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { Skeleton } from "./ui/skeleton";
import { Stack } from "./ui/stack";

export interface FormInputProps extends InputProps {
  name: string;
  error?: string;
  info?: string;
  fullWidth?: boolean;
  disableRequirementAsterisk?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  isLoading?: boolean;
  hideAutomaticError?: boolean;
  hideErrorMessage?: boolean;
}

const getNestedValue = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any>,
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const parts = name.split(/[[\].]/).filter(Boolean);
  let current = obj;

  if (!current || Object.keys(current).length === 0) {
    return undefined;
  }

  for (const part of parts) {
    if (current === undefined || current === null) {
      return undefined;
    }
    current = current[part];
  }

  return current;
};

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      error,
      info,
      name,
      fullWidth,
      isLoading,
      hideAutomaticError = false,
      hideErrorMessage = false,
      onChange,
      onInput,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const { errors } = useFormikContext<Record<string, unknown>>();
    const [hasTyped, setHasTyped] = React.useState(false);
    const [hasBlurred, setHasBlurred] = React.useState(false);

    if (isLoading) {
      return <Skeleton className={cn("h-14", fullWidth && "w-full")} />;
    }

    const formikError = !hideAutomaticError
      ? getNestedValue(errors, name)
      : undefined;
    const showError =
      !hideAutomaticError && hasTyped && hasBlurred && formikError;

    const handleTypedStateUpdate = (value: string) => {
      if (!hasTyped && value.length > 0) {
        setHasTyped(true);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleTypedStateUpdate(e.target.value);
      if (onChange) {
        onChange(e);
      }
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      handleTypedStateUpdate(e.currentTarget.value);
      if (onInput) {
        onInput(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Only set hasBlurred if the input has content (user actually tried to fill it)
      if (e.target.value.length > 0) {
        setHasBlurred(true);
      }
      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <Stack gap="xs" className={fullWidth ? "w-full" : ""}>
        <Input
          error={showError ? formikError : error}
          className={cn(
            error && "border-destructive focus-visible:ring-destructive/50",
            className,
          )}
          ref={ref}
          fullWidth={fullWidth}
          name={name}
          onChange={handleInputChange}
          onInput={handleInput}
          onBlur={handleBlur}
          {...props}
        />
        {error && !hideErrorMessage && (
          <Typography variant="bodyW2" className="text-destructive">
            {error}
          </Typography>
        )}
        {showError && !error && !hideErrorMessage && (
          <Typography variant="bodyW2" className="text-destructive">
            {formikError}
          </Typography>
        )}
        {!error && info && (
          <Typography variant="bodyW2" className="text-tertiary-text">
            {info}
          </Typography>
        )}
      </Stack>
    );
  },
);

FormInput.displayName = "FormInput";

export { FormInput };
